import { isServerless } from 'helpers';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { ObjectMap } from 'types';

export const getQueryString = (params: ObjectMap) => {
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach(
    (key) => params[key] && searchParams.append(key, params[key]),
  );
  return searchParams.toString();
};

export type QueryConfig<Q extends {} = ObjectMap> = {
  [K in keyof Q]?: (value?: string) => Q[K];
};

function paramsToObject<T extends { [key: string]: string } = ObjectMap>(
  searchParams: URLSearchParams,
  config?: QueryConfig<T>,
): T {
  const result: ObjectMap = {};
  for (const [key, value] of searchParams.entries()) {
    result[key] = config?.[key as keyof QueryConfig<T>]
      ? config[key as keyof QueryConfig<T>]?.(value)
      : value;
  }
  return result as T;
}

export const getQueryParams = <T extends {}>(
  searchString?: string,
  config?: QueryConfig<T>,
): Partial<T> => {
  if (!searchString) {
    return {};
  }
  const urlParams = new URLSearchParams(searchString);
  const params = paramsToObject(urlParams, config);
  return params;
};

/**
 * @fact
 * next-router is cache by nginx and return in `getServerSideProps`, `getInitialProps`
 * when we use useRouter hook, this means `router.query` is fetched from cache
 *
 * @solution
 * get the correct query from current URL instead of cache.
 */
export const useQuery = <T extends {}>(config?: QueryConfig<T>) => {
  const router = useRouter(); /** use in server */
  const search = isServerless()
    ? window?.location?.search
    : getQueryString(router.query);

  const query = useMemo((): Partial<T> => {
    try {
      return getQueryParams(search, config);
    } catch (e) {
      return {};
    }
  }, [search]);

  return query;
};

export const NumberParam = (value?: string) => {
  try {
    if (!value) return null;
    return Number(value);
  } catch {
    return;
  }
};
export const StringParam = (value?: string) => value;

export const ArrayParams =
  <T,>(func: (value: string) => T) =>
  (value: string, splitChar = ',') =>
    value.split(splitChar).map(func);
